import {
  ApiBlueprint,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { OAuth2 } from '@backstage/core-app-api';
import { compatWrapper, convertLegacyPlugin } from '@backstage/core-compat-api';
import {
  coderAuthApiRef,
  coderPlugin as legacyCoderPlugin,
} from '@coder/backstage-plugin-coder';
import CodeIcon from '@material-ui/icons/Code';
import { isCoderWorkspaceEntity } from './entityWorkspaceDefaults';

const coderRouteRef = createRouteRef();

const coderPage = PageBlueprint.make({
  params: {
    path: '/coder',
    title: 'Coder',
    icon: <CodeIcon />,
    routeRef: coderRouteRef,
    noHeader: true,
    loader: async () => {
      const { CoderPage } = await import('./CoderPage');
      // Coder components are legacy-frontend; wrap so legacy APIs resolve.
      return compatWrapper(<CoderPage />);
    },
  },
});

// Provide coderAuthApiRef as an OAuth2 client so the workspaces UI authenticates
// to Coder via OAuth2 (connected account). The Coder plugin is consumed only via
// compatWrapper, so no default implementation is registered — this blueprint is
// the sole provider, hence no override conflict.
const coderAuthApi = ApiBlueprint.make({
  name: 'coder-auth',
  params: defineParams =>
    defineParams({
      api: coderAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'coder',
            title: 'Coder',
            icon: () => null,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: [],
        }),
    }),
});

// Coder workspaces card on System pages and DAH workspace Resource pages.
// Workspace defaults are derived from standard Backstage location annotations.
const coderWorkspacesCard = EntityCardBlueprint.make({
  name: 'workspaces',
  params: {
    type: 'content',
    filter: isCoderWorkspaceEntity,
    loader: async () => {
      const { CoderWorkspacesEntityCard } =
        await import('./CoderWorkspacesEntityCard');
      // Coder components are legacy-frontend; wrap so legacy APIs resolve.
      return compatWrapper(<CoderWorkspacesEntityCard />);
    },
  },
});

// Convert the whole legacy plugin (not just a module) so its api factories —
// coderClientWrapperApiRef (`coder`) and urlSyncApiRef — are registered in the
// new frontend system; the components need them at runtime. A bare
// createFrontendModule would have nothing to attach to (no new-system `coder`
// plugin exists) and its extensions would be silently dropped. Our extensions,
// including the OAuth2 coderAuthApiRef provider, are added on top.
export const coderPlugin = convertLegacyPlugin(legacyCoderPlugin, {
  extensions: [coderPage, coderAuthApi, coderWorkspacesCard],
});
